import { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import User, { UserFilters } from "@/interfaces/IUser";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FC, useId } from "react";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { GroupBase, MultiValue, OptionsOrGroups } from "react-select";

export type UserFilterRole = "AE" | "SDR" | "CSM";

export const USER_FILTER_LABELS: Record<UserFilterRole, string> = {
  AE: "Commercial :",
  SDR: "Télépro :",
  CSM: "CSM :",
};

const SELF_LABEL = "Moi";

/**
 * Rôle au sens strict : contrairement à checkIfUserHasRoles, ADMIN et CSM ne
 * valent pas commercial ici. Se filtrer comme commercial sans l'être ne
 * ramènerait rien.
 */
export const canFilterBySelf = (
  user: User | null | undefined,
  role: UserFilterRole,
) => !!user?.roles?.includes(role);

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

/** « Moi » reste proposé tant que la recherche peut le désigner. */
const searchDesignatesSelf = (search: string, self: User) => {
  const query = normalize(search);
  return (
    !query ||
    normalize(SELF_LABEL).startsWith(query) ||
    normalize(`${self.firstname} ${self.lastname}`).includes(query)
  );
};

/**
 * Ce qui part dans les filtres, donc dans l'URL du calendrier ou le
 * localStorage des tableaux : l'id pour l'API, le nom pour le libellé. Le
 * profil complet de la session alourdirait l'URL pour rien.
 */
const toFilterValue = ({
  id,
  firstname,
  lastname,
  fullname,
  roles,
  isActive,
}: User) => ({ id, firstname, lastname, fullname, roles, isActive }) as User;

interface UserRoleFilterProps {
  role: UserFilterRole;
  /** `all` : tous les utilisateurs du rôle. `pool` : l'entourage seulement
   *  (/users/calendar-filterable), qui exclut l'utilisateur connecté. */
  source: "all" | "pool";
  value?: MultiValue<User>;
  onChange: (users: MultiValue<User>) => void;
}

/**
 * Sélecteur d'utilisateurs d'un rôle (Commercial, Télépro, CSM) des panneaux
 * de filtres. Quand l'utilisateur connecté a lui-même ce rôle, il s'y trouve
 * en tête sous le nom « Moi », plutôt que perdu dans la liste — ou absent,
 * l'entourage l'excluant.
 */
const UserRoleFilter: FC<UserRoleFilterProps> = ({
  role,
  source,
  value,
  onChange,
}) => {
  const inputId = useId();
  const currentUser = useSelector(selectCurrentUser);
  const { loadUsersOptions, loadCalendarFilterableOptions } = useLoadOptions();
  const load =
    source === "all" ? loadUsersOptions : loadCalendarFilterableOptions;
  const self = canFilterBySelf(currentUser, role) ? currentUser : null;

  const loadOptions = async (
    search: string,
    loaded: OptionsOrGroups<User, GroupBase<User>>,
    additional?: UserFilters,
  ) => {
    const response = await load(search, loaded, additional);
    const others = response.options.filter(
      (user) => user.id !== currentUser?.id,
    );
    const pinSelf =
      !!self && !loaded.length && searchDesignatesSelf(search, self);
    return {
      ...response,
      options: pinSelf ? [toFilterValue(self), ...others] : others,
    };
  };

  return (
    <Form.Group className="mt-2">
      <Form.Label htmlFor={inputId} className="mb-1 fs--1">
        {USER_FILTER_LABELS[role]}
      </Form.Label>
      <SelectPaginateAsync
        inputId={inputId}
        loadOptions={loadOptions}
        additional={{
          pageIndex: 0,
          roles: [role],
          // Un télépro désactivé n'a plus de prospection à suivre.
          ...(role === "SDR" && { isActive: true }),
        }}
        getOptionLabel={(option: User) =>
          option.id === currentUser?.id
            ? SELF_LABEL
            : `${option.firstname} ${option.lastname}`
        }
        getOptionValue={(option: User) => option.id.toString()}
        value={value}
        isClearable
        isMulti
        onChange={(users) => onChange(users as MultiValue<User>)}
      />
    </Form.Group>
  );
};

export default UserRoleFilter;
